/**
 * The "WhatsApp Business" card on realtor settings (U8; sketch:
 * docs/design/2026-10-12-realtor-whatsapp-capture-sketch.md §1).
 *
 * Not connected, or disconnected: what is and isn't recorded, a suggested
 * client notice with a copy button, a required notice confirmation, and
 * Connect, which runs Meta's Embedded Signup and sends its result to the
 * backend. Connected or broken: the number, the earlier-chats import (a
 * warning in its place when broken), the last message, the chats waiting for
 * review, and Disconnect.
 *
 * Absent unless the server has WhatsApp capture switched on and this build
 * carries the Meta config. Nothing shows while the status loads.
 */

import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Copy, Loader2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { SyncProgressBar } from "@/features/realtor/components/whatsapp/sync-progress-bar";
import {
	useConnectWhatsapp,
	useDisconnectWhatsapp,
	useWhatsappCapture,
} from "@/features/realtor/hooks/use-whatsapp";
import {
	type WhatsappConnection,
	whatsappErrorCode,
} from "@/features/realtor/services/whatsapp-api.service";
import {
	EmbeddedSignupError,
	type EmbeddedSignupFailure,
	type EmbeddedSignupResult,
	runEmbeddedSignup,
} from "@/features/realtor/utils/meta-sdk";
import { formatRelativeTime } from "@/features/realtor/utils/relative-time";
import { copyToClipboard } from "@/features/realtor/utils/share-listing";
import { StudioButton } from "@/features/studio/theme";
import { queryKeys } from "@/lib/query-keys";

/**
 * The wording of the client notice the realtor confirms, sent with connect.
 * Raise it when `realtor.settings.whatsapp.noticeText` or `noticeConfirm`
 * changes meaning.
 */
export const CLIENT_NOTICE_VERSION = 1;

/** The review page's route (routes.tsx). */
export const WHATSAPP_REVIEW_PATH = "/realtor/whatsapp-review";

const T = "realtor.settings.whatsapp";

type FailureKey =
	| "sdkLoadFailed"
	| "popupBlocked"
	| "noWindow"
	| "insecurePage"
	| "timeout"
	| "metaError"
	| "codeExpired"
	| "numberNotEligible"
	| "numberInUse"
	| "wabaNotGranted"
	| "metaUnavailable"
	| "unavailable"
	| "generic";

const SIGNUP_FAILURE: Record<
	Exclude<EmbeddedSignupFailure, "cancelled">,
	FailureKey
> = {
	sdk_load_failed: "sdkLoadFailed",
	popup_blocked: "popupBlocked",
	no_window: "noWindow",
	insecure_page: "insecurePage",
	timeout: "timeout",
	meta_error: "metaError",
};

const CONNECT_FAILURE: Record<string, FailureKey> = {
	CODE_EXPIRED: "codeExpired",
	NUMBER_NOT_ELIGIBLE: "numberNotEligible",
	NUMBER_IN_USE: "numberInUse",
	WABA_NOT_GRANTED: "wabaNotGranted",
	META_UNAVAILABLE: "metaUnavailable",
	WHATSAPP_CAPTURE_DISABLED: "unavailable",
};

export interface WhatsappConnectionCardProps {
	/** Runs Meta's sign-up; tests pass a stand-in. */
	runSignup?: () => Promise<EmbeddedSignupResult>;
}

export function WhatsappConnectionCard({
	runSignup = runEmbeddedSignup,
}: WhatsappConnectionCardProps) {
	const { t } = useTranslation();
	const { status } = useWhatsappCapture();

	// Without the Meta config the query is disabled and stays pending.
	if (status.isPending) return null;
	if (status.isError) {
		return (
			<CardShell>
				<QueryErrorState
					message={t(`${T}.loadFailed`)}
					onRetry={() => void status.refetch()}
					isRetrying={status.isFetching}
				/>
			</CardShell>
		);
	}
	if (!status.data.available) return null;

	const connection = status.data.connection;
	if (connection && connection.status !== "disconnected") {
		return (
			<CardShell
				pill={
					connection.status === "broken" ? (
						<Pill tone="awaiting">{t(`${T}.statusBroken`)}</Pill>
					) : (
						<Pill tone="done">{t(`${T}.statusConnected`)}</Pill>
					)
				}
			>
				<ConnectedBody connection={connection} />
			</CardShell>
		);
	}
	return (
		<CardShell>
			{connection?.disconnectReason === "partner_removed" && (
				<Notice tone="awaiting">{t(`${T}.removed`)}</Notice>
			)}
			<ConnectForm runSignup={runSignup} />
		</CardShell>
	);
}

function CardShell({
	pill,
	children,
}: {
	pill?: ReactNode;
	children: ReactNode;
}) {
	const { t } = useTranslation();
	return (
		<Card
			className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]"
			data-testid="whatsapp-connection-card"
		>
			<CardContent className="space-y-4 p-4">
				<div className="flex flex-wrap items-start justify-between gap-2">
					<div className="min-w-0">
						<h2 className="text-sm font-medium text-dashboard-text">
							{t(`${T}.title`)}
						</h2>
						<p className="text-xs text-dashboard-muted">
							{t(`${T}.description`)}
						</p>
					</div>
					{pill}
				</div>
				{children}
			</CardContent>
		</Card>
	);
}

function Pill({
	tone,
	children,
}: {
	tone: "done" | "awaiting";
	children: ReactNode;
}) {
	const colours =
		tone === "done"
			? "bg-dashboard-pill-done-bg text-dashboard-pill-done"
			: "bg-dashboard-pill-awaiting-bg text-dashboard-pill-awaiting";
	return (
		<span
			className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${colours}`}
		>
			{children}
		</span>
	);
}

/** A tinted box: "awaiting" for warnings, "risk" for errors (role="alert"). */
function Notice({
	tone,
	children,
}: {
	tone: "awaiting" | "risk";
	children: ReactNode;
}) {
	const box =
		tone === "risk"
			? "border-dashboard-pill-risk bg-dashboard-pill-risk-bg"
			: "border-dashboard-pill-awaiting bg-dashboard-pill-awaiting-bg";
	const icon =
		tone === "risk"
			? "text-dashboard-pill-risk"
			: "text-dashboard-pill-awaiting";
	return (
		<div
			className={`flex items-start gap-2 rounded-md border p-3 ${box}`}
			role={tone === "risk" ? "alert" : undefined}
		>
			<AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${icon}`} />
			<div className="min-w-0 space-y-1 text-xs text-dashboard-text">
				{children}
			</div>
		</div>
	);
}

type Phase = "idle" | "signup" | "connecting";

function ConnectForm({
	runSignup,
}: {
	runSignup: () => Promise<EmbeddedSignupResult>;
}) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const connect = useConnectWhatsapp();
	const [confirmed, setConfirmed] = useState(false);
	const [phase, setPhase] = useState<Phase>("idle");
	const [failure, setFailure] = useState<FailureKey | null>(null);
	const busy = phase !== "idle";

	const onConnect = async () => {
		setFailure(null);
		setPhase("signup");
		let result: EmbeddedSignupResult;
		try {
			result = await runSignup();
		} catch (error) {
			setPhase("idle");
			const reason =
				error instanceof EmbeddedSignupError ? error.reason : "meta_error";
			if (reason !== "cancelled") setFailure(SIGNUP_FAILURE[reason]);
			return;
		}
		setPhase("connecting");
		connect.mutate(
			{ ...result, noticeVersion: CLIENT_NOTICE_VERSION },
			{
				onSuccess: () => toast.success(t(`${T}.connectedToast`)),
				onError: (error) => {
					const code = whatsappErrorCode(error);
					setFailure((code && CONNECT_FAILURE[code]) || "generic");
					if (code === "WHATSAPP_CAPTURE_DISABLED") {
						void queryClient.invalidateQueries({
							queryKey: queryKeys.realtor.whatsappConnection(),
						});
					}
				},
				onSettled: () => setPhase("idle"),
			},
		);
	};

	const copyNotice = async () => {
		const ok = await copyToClipboard(t(`${T}.noticeText`));
		if (ok) toast.success(t("realtor.settings.copy.copiedToast"));
		else toast.error(t("realtor.settings.copy.failed"));
	};

	return (
		<div className="space-y-4">
			<div className="space-y-1">
				<h3 className="text-xs font-medium text-dashboard-text">
					{t(`${T}.recordsHeading`)}
				</h3>
				<ul className="list-disc space-y-1 pl-5 text-xs text-dashboard-muted">
					<li>{t(`${T}.records.contacts`)}</li>
					<li>{t(`${T}.records.review`)}</li>
					<li>{t(`${T}.records.history`)}</li>
					<li>{t(`${T}.records.media`)}</li>
				</ul>
				<h3 className="pt-2 text-xs font-medium text-dashboard-text">
					{t(`${T}.neverHeading`)}
				</h3>
				<p className="text-xs text-dashboard-muted">{t(`${T}.never`)}</p>
			</div>

			<div className="space-y-2 rounded-md bg-dashboard-surface p-3">
				<p className="text-xs font-medium text-dashboard-text">
					{t(`${T}.noticeHeading`)}
				</p>
				<p className="text-sm text-dashboard-text">{t(`${T}.noticeText`)}</p>
				<div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
					<p className="text-xs text-dashboard-muted">
						{t(`${T}.noticeWhere`)}
					</p>
					<StudioButton
						variant="secondary"
						size="sm"
						icon={Copy}
						className="w-full shrink-0 sm:w-auto"
						onClick={() => void copyNotice()}
					>
						{t(`${T}.copyNotice`)}
					</StudioButton>
				</div>
			</div>

			<label className="flex min-h-11 cursor-pointer items-start gap-2 text-sm text-dashboard-text sm:min-h-0">
				<input
					type="checkbox"
					checked={confirmed}
					disabled={busy}
					onChange={(event) => setConfirmed(event.target.checked)}
					className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-dashboard-accent)]"
				/>
				{t(`${T}.noticeConfirm`)}
			</label>

			{failure && (
				<Notice tone="risk">
					<p>{t(`${T}.errors.${failure}`)}</p>
					<p className="text-dashboard-muted">
						{t(`${T}.errors.fallbackHint`)}{" "}
						<Link
							to="/realtor/contacts"
							className="font-medium text-dashboard-accent-dark underline"
						>
							{t(`${T}.errors.fallbackLink`)}
						</Link>
					</p>
				</Notice>
			)}

			<StudioButton
				variant="primary"
				className="w-full gap-1.5 sm:w-auto"
				disabled={!confirmed || busy}
				aria-busy={busy || undefined}
				onClick={() => void onConnect()}
			>
				{busy && <Loader2 className="h-4 w-4 animate-spin" />}
				{phase === "signup"
					? t(`${T}.connectingMeta`)
					: phase === "connecting"
						? t(`${T}.connectingNumber`)
						: t(`${T}.connect`)}
			</StudioButton>
		</div>
	);
}

function ConnectedBody({ connection }: { connection: WhatsappConnection }) {
	const { t, i18n } = useTranslation();
	const [confirming, setConfirming] = useState(false);
	const disconnect = useDisconnectWhatsapp();
	const broken = connection.status === "broken";

	const onDisconnect = () => {
		disconnect.mutate(undefined, {
			onSuccess: () => {
				setConfirming(false);
				toast.success(t(`${T}.disconnectedToast`));
			},
			onError: () => toast.error(t(`${T}.disconnectFailed`)),
		});
	};

	return (
		<div className="space-y-3">
			<p className="font-mono text-sm text-dashboard-text">
				{connection.displayPhoneNumber}
			</p>

			{broken ? (
				<Notice tone="awaiting">{t(`${T}.broken`)}</Notice>
			) : (
				<SyncRow connection={connection} />
			)}

			<p className="text-xs text-dashboard-muted">
				{connection.lastMessageAt
					? t(`${T}.lastMessage`, {
							time: formatRelativeTime(
								connection.lastMessageAt,
								i18n.language,
								t("realtor.time.justNow"),
							),
						})
					: t(`${T}.noMessages`)}
			</p>

			{connection.reviewEntriesLeft > 0 && (
				<div className="flex items-center justify-between gap-3 rounded-md bg-dashboard-surface px-3 py-2">
					<p className="text-sm text-dashboard-text">
						{t(`${T}.reviewCount`, { count: connection.reviewEntriesLeft })}
					</p>
					<Link
						to={WHATSAPP_REVIEW_PATH}
						className="text-sm font-medium text-dashboard-accent-dark underline"
					>
						{t(`${T}.review`)}
					</Link>
				</div>
			)}

			<div className="flex sm:justify-end">
				<StudioButton
					variant="secondary"
					className="w-full sm:w-auto"
					onClick={() => setConfirming(true)}
				>
					{t(`${T}.disconnect`)}
				</StudioButton>
			</div>

			<AlertDialog
				open={confirming}
				onOpenChange={(open) => !disconnect.isPending && setConfirming(open)}
			>
				<AlertDialogContent className="bg-dashboard-bg border-dashboard-border rounded-xl">
					<AlertDialogHeader>
						<AlertDialogTitle className="text-dashboard-text">
							{t(`${T}.disconnectDialog.title`)}
						</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className="space-y-2 text-sm text-dashboard-muted">
								<p>{t(`${T}.disconnectDialog.body`)}</p>
								{connection.reviewEntriesLeft > 0 && (
									<p>
										{t(`${T}.disconnectDialog.review`, {
											count: connection.reviewEntriesLeft,
										})}
									</p>
								)}
								<p>{t(`${T}.disconnectDialog.app`)}</p>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel
							disabled={disconnect.isPending}
							className="border-dashboard-border text-dashboard-text"
						>
							{t(`${T}.disconnectDialog.keep`)}
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={disconnect.isPending}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							onClick={(event) => {
								event.preventDefault();
								onDisconnect();
							}}
						>
							{disconnect.isPending && (
								<Loader2 className="h-4 w-4 animate-spin" />
							)}
							{t(`${T}.disconnectDialog.confirm`)}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

function SyncRow({ connection }: { connection: WhatsappConnection }) {
	const { t } = useTranslation();
	switch (connection.syncState) {
		case "pending":
		case "syncing":
			return (
				<SyncProgressBar
					syncState={connection.syncState}
					historyProgress={connection.historyProgress}
				/>
			);
		case "complete":
			return (
				<p className="text-sm text-dashboard-text">{t(`${T}.sync.complete`)}</p>
			);
		case "declined":
			return (
				<p className="text-sm text-dashboard-text">{t(`${T}.sync.declined`)}</p>
			);
		case "failed":
			return <Notice tone="awaiting">{t(`${T}.sync.failed`)}</Notice>;
	}
}
